<template>
  <SearchResult
    v-if="route.query.searchQuery"
    :is-pending="'success' !== status"
  /> 
  <template v-else-if="undefined !== category">
    <Breadcrumbs :breadcrumbs="breadcrumbs" />
    <Promotions
      v-if="category.code === promotionCategoryCode"
      :category="category"
    />
    <template v-else>
      <div class="product-catalog">
        <PromotionBanner
          v-if="category.parent?.code === promotionCategoryCode"
          :category="category"
        />
        <template v-else>
          <h1 class="product-catalog__title">
            {{ category.getName() }}
          </h1>
        </template>
        <CategoryShowcase
          v-if="children(category).length > 0"
          :categories="children(category)"
        />
      </div>
      <ProductCatalog :category="category" :is-pending="'success' !== status" />
    </template>
  </template>
</template>

<script setup>
import Breadcrumbs from "~/modules/shared/breadcrumbs/Breadcrumbs.vue";
import { useCategoriesStore } from "~/store/categoriesStore";
import ProductCatalog from "~/modules/productCatalog/ui/components/ProductCatalog.vue";
import { useProductStore } from "~/store/productStore";
import { useCriteriaStore } from "~/store/criteriaStore";
import { promotionCategoryCode } from "~/config/category.config";
import Promotions from "~/modules/productCatalog/ui/pages/promotions/Promotions.vue";
import PromotionBanner from "~/modules/productCatalog/ui/components/PromotionBanner.vue";
import { useFilterStore } from "~/store/filterStore";
import SearchResult from "~/modules/productCatalog/ui/pages/search/SearchResult.vue";
import CategoryShowcase from "~/modules/productCatalog/ui/components/CategoryShowcase.vue";

definePageMeta({
  key: "category",
});

// Подключение сторов
const route = useRoute();
const categoryStore = useCategoriesStore();
const filterStore = useFilterStore();
const criteriaStore = useCriteriaStore();
const productStore = useProductStore();

const { getChildren: children } = storeToRefs(categoryStore);

const { totalItems } = storeToRefs(productStore);

const category = ref(null);
const breadcrumbs = ref([]);

const buildBreadcrumbs = (category) => {
  breadcrumbs.value.unshift({
    name: category.name,
    routeName: "category",
    params: {
      categorySlug: category.slug,
    },
  });

  if (null !== category.parent) {
    return buildBreadcrumbs(category.parent);
  }

  breadcrumbs.value.unshift({
    name: "@Catalog",
    routeName: "catalog",
  });
};

const getJsonLd = (category, criteria) => {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: category.getName(),
    image: category.getImageUrl("catalogImage"),
    offers: {
      "@type": "AggregateOffer",
      availability: "https://schema.org/InStock",
      lowPrice: criteria?.priceRange.min ?? 1,
      highPrice: criteria?.priceRange.max ?? 1,
      priceCurrency: "UAH",
      offerCount: totalItems.value,
    },
  };
};

watch(
  [() => route.params.categorySlug, () => route.query.searchQuery],
  async () => {
    category.value = categoryStore.getCategoryBySlug(route.params.categorySlug);

    breadcrumbs.value = [];
    if (undefined === route.query.searchQuery) {
      buildBreadcrumbs(category.value);
    }

    filterStore.initParams({ categoryId: category.value?.id });

    if (undefined !== category.value) {
      configureCategorySeo(category.value);
    }
  },
  { immediate: true },
);

const { status } = await useAsyncData(
  "fetchAll",
  async () => {
    if (undefined === category.value && 0 === Object.keys(route.query).length) {
      return null;
    }

    return await Promise.all([
      new Promise((resolve) => resolve(productStore.fetch())),
      new Promise((resolve) => resolve(criteriaStore.fetch())),
      new Promise((resolve) =>
        undefined !== category.value
          ? resolve(categoryStore.fetchContent(category.value.id))
          : resolve(),
      ),
    ]);
  },
  { watch: [() => route.params.categorySlug, () => route.query] },
);

if (
  undefined !== category.value &&
  undefined === route.query.searchQuery &&
  import.meta.server
) {
  useHead({
    script: [
      {
        type: "application/ld+json",
        children: getJsonLd(category.value, criteriaStore.criteria),
      },
    ],
  });
}
</script>
